import React, { useState } from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import PropTypes from 'prop-types';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core/styles';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import NestedMenuItem from './NestedMenuItem';

const StyledMenu = styled(Menu)`
  pointer-events: none;

  & > .MuiPaper-root {
    top: 65px !important;
  }
`;

const MenuItemContainer = styled.div`
  pointer-events: auto;
`;

const StyledNavLink = styled(NavLink)`
  flex: 0 1 auto;
  color: #484848;
  text-decoration: none;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 18px;
  padding: 14px 32px;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  word-break: keep-all;
`;

const StyledMenuLink = styled.div`
  flex: 0 1 auto;
  color: ${({ isActive, activeStyle }) => (isActive ? activeStyle.color : '#484848')};
  text-decoration: none;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 18px;
  padding: 6px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  word-break: keep-all;
  cursor: pointer;
  background-color: ${({ isMenuOpen, menuOpenStyle }) => (isMenuOpen ? menuOpenStyle.backgroundColor : 'unset')};
`;

const StyledMenuItem = withStyles(() => ({
  root: {
    padding: 0,
    fontFamily: 'inherit',
  },
}))(MenuItem);

const StyledNestedMenuItem = styled(NestedMenuItem)`
  font-family: inherit;
  position: relative;

  & > div {
    flex: 0 1 auto;
    color: #484848;
    text-decoration: none;
    font-style: normal;
    font-weight: bold;
    font-size: 18px;
    line-height: 18px;
    padding: 6px 24px;
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    word-break: keep-all;
  }
`;

const NameContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 10px;
  text-align: center;
  white-space: pre-line;
`;

const NestedExpandMore = styled(ExpandMore)`
  top: 0;
  right: 5%;
  transform: rotate(-90deg) translate(-42%, 0);
  position: absolute;
`;

const NestedMenuItemLabel = styled.div`
  color: ${({ isActive, activeStyle }) => (isActive ? activeStyle.color : '#484848')} !important;
`;

const SubLinkRender = ({
  link, parentMenuOpen, handleClose, resolvedActiveStyle,
}) => {
  const location = useLocation();

  if (link.type !== 'nested') {
    if (link.newTab) {
      return (
        <StyledMenuItem key={link.name} onClick={handleClose}>
          <StyledNavLink
            as="a"
            to="#"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={link.onClick}
          >
            <NameContainer>{ReactHtmlParser(link.name)}</NameContainer>
          </StyledNavLink>
        </StyledMenuItem>
      );
    }
    return (
      <StyledMenuItem key={link.name} onClick={handleClose}>
        <StyledNavLink
          onClick={link.onClick}
          to={link.to}
          activeStyle={resolvedActiveStyle}
          isActive={(match, currentLocation) => !!(
            matchPath(currentLocation.pathname, {
              path: [link.to],
              exact: true,
            })
          )}
        >
          <NameContainer>{ReactHtmlParser(link.name)}</NameContainer>
        </StyledNavLink>
      </StyledMenuItem>
    );
  }

  return (
    <StyledNestedMenuItem
      label={(
        <NestedMenuItemLabel
          isActive={link.isActive(location)}
          activeStyle={resolvedActiveStyle}
        >
          <NameContainer>
            <div>{ReactHtmlParser(link.name)}</div>
          </NameContainer>
          <NestedExpandMore />
        </NestedMenuItemLabel>
      )}
      parentMenuOpen={parentMenuOpen}
      onClick={handleClose}
    >
      {(link.subLinks || []).map((subLink, index) => (
        <SubLinkRender
          key={index}
          link={subLink}
          parentMenuOpen={parentMenuOpen}
          handleClose={handleClose}
          resolvedActiveStyle={resolvedActiveStyle}
        />
      ))}
    </StyledNestedMenuItem>
  );
};

SubLinkRender.propTypes = {
  link: PropTypes.shape().isRequired,
  parentMenuOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  resolvedActiveStyle: PropTypes.shape().isRequired,
};

const AppBarMenuLink = ({
  name, isActive, onClick, htmlId,
  activeStyle, showMenu, subLinks, menuOpenStyle,
  icon,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const { themeColor } = useSelector((s) => s.eventConfigs);

  let resolvedActiveStyle = activeStyle;

  if (!activeStyle) {
    resolvedActiveStyle = {
      color: themeColor || DEFAULT_THEME_COLOR,
    };
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    onClick();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div id={`${htmlId}-container`} onMouseEnter={handleClick} onMouseLeave={handleClose}>
      <StyledMenuLink
        id={htmlId}
        onClick={handleClick}
        activeStyle={resolvedActiveStyle}
        isActive={isActive(location)}
        isMenuOpen={Boolean(anchorEl)}
        menuOpenStyle={menuOpenStyle}
      >
        {(name) && (
          <NameContainer>
            <div>{ReactHtmlParser(name)}</div>
          </NameContainer>
        )}
        {icon}
      </StyledMenuLink>
      {showMenu && Boolean(subLinks.length) && (
        <StyledMenu
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          autoFocus={false}
          disableAutoFocus
          disableEnforceFocus
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          MenuListProps={{ onMouseLeave: handleClose }}
        >
          <MenuItemContainer>
            {subLinks.map((link, index) => (
              <SubLinkRender
                key={index}
                link={link}
                parentMenuOpen={Boolean(anchorEl)}
                handleClose={handleClose}
                resolvedActiveStyle={resolvedActiveStyle}
              />
            ))}
          </MenuItemContainer>
        </StyledMenu>
      )}
    </div>
  );
};

AppBarMenuLink.propTypes = {
  htmlId: PropTypes.string,
  name: PropTypes.string,
  isActive: PropTypes.func,
  activeStyle: PropTypes.shape({ color: PropTypes.string.isRequired }),
  onClick: PropTypes.func,
  showMenu: PropTypes.bool,
  subLinks: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    translatedName: PropTypes.string,
    onClick: PropTypes.func,
    to: PropTypes.string,
  })),
  menuOpenStyle: PropTypes.shape({ backgroundColor: PropTypes.string.isRequired }),
  icon: PropTypes.node,
};

AppBarMenuLink.defaultProps = {
  htmlId: '',
  name: '',
  isActive: () => false,
  activeStyle: null,
  onClick: () => { },
  showMenu: true,
  subLinks: [],
  menuOpenStyle: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  icon: <ExpandMore />,
};

export default AppBarMenuLink;
